import { restClient } from '@/Core'

/**
 * Завантажує файл з API й віддає його браузеру на збереження (`SPEC-008` §5.9,
 * ADR 033; легасі `frontend/src/Ship/Tasks/ExportToExcelTask.js:6-39`).
 *
 * Звичайне посилання тут не годиться: ендпойнт закритий JWT, а токен живе в
 * ядровому клієнті, не в cookie. Тому файл тягнемо тим самим `restClient` —
 * він сам підставляє `Authorization` і раз оновлює токен на 401, — але без
 * усього, що він робить із JSON:
 *
 *  - `isOriginalResponse` — віддати сирий `Response`, а не розбирати тіло як JSON;
 *  - `disableResponseTransform` — глобальний трансформер відповіді з
 *    `ServiceConfig` теж читає тіло як JSON і на бінарному файлі падає;
 *  - `isDisableResponseCache` — ядро на пів хвилини кешує GET за ключем
 *    запиту, і повторне «Експорт» віддало б старий файл.
 *
 * Помилку сервера (403, 422) перекладаємо у форму, яку читає
 * `serverMessage()`: розібране тіло — у `response`, код — у `statusCode`.
 */

export interface DownloadFileParams {
    /** Адреса API, наприклад `/services/clients/export`. */
    url: string
    /** Параметри запиту — ті самі, що в списку (`filters`, `q`, `sort`). */
    searchParams?: Record<string, string | number | boolean>
    /**
     * Назва файлу для користувача. Без неї — з `Content-Disposition`
     * (`filename*` має перевагу над ASCII-запасною `filename`).
     */
    fileName?: string
    /** Межа очікування, мс: великий реєстр формується довше за типові 30 с. */
    timeout?: number
}

/** Скільки чекати на файл за замовчуванням — з запасом під межу PHP у 60 с. */
export const DOWNLOAD_TIMEOUT_MS = 90_000

interface DownloadError extends Error {
    response?: unknown
    statusCode?: number
}

/** Назва файлу із заголовка `Content-Disposition`; `null` — немає. */
export const fileNameFromDisposition = (header: string | null): string | null => {
    if (!header) return null

    const encoded = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(header)?.[1]

    if (encoded) {
        try {
            return decodeURIComponent(encoded.trim().replace(/^"|"$/g, ''))
        } catch {
            // Зіпсоване кодування — беремо запасну назву нижче
        }
    }

    const plain = /filename\s*=\s*("?)([^";]+)\1/.exec(header)?.[2]

    return plain ? plain.trim() : null
}

/** Зберегти Blob як файл: тимчасове посилання з `download`. */
const saveBlob = (blob: Blob, fileName: string): void => {
    const href = URL.createObjectURL(blob)
    const link = document.createElement('a')

    link.href = href
    link.download = fileName
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    link.remove()

    // Браузер уже забрав посилання — звільняємо пам'ять трохи згодом
    setTimeout(() => URL.revokeObjectURL(href), 1000)
}

/** Помилку ky з сирим `Response` — у форму `{ response: тіло, statusCode }`. */
const toServerError = async (error: unknown): Promise<unknown> => {
    const raw = (error as { response?: unknown } | null)?.response

    if (!(raw instanceof Response)) return error

    let body: unknown = null

    try {
        body = await raw.clone().json()
    } catch {
        try {
            body = await raw.text()
        } catch {
            body = null
        }
    }

    const wrapped: DownloadError = new Error('Не вдалося завантажити файл.')
    wrapped.response = body
    wrapped.statusCode = raw.status

    return wrapped
}

export const DownloadFileTask = async ({
    url,
    searchParams = {},
    fileName,
    timeout = DOWNLOAD_TIMEOUT_MS,
}: DownloadFileParams): Promise<void> => {
    const request = restClient.get(
        url,
        { searchParams, timeout },
        {
            isOriginalResponse: true,
            disableResponseTransform: true,
            isDisableResponseCache: true,
            isNotHeaderAddContentType: true,
        }
    )

    if (!request) throw new Error('Не вдалося створити запит на завантаження файлу.')

    let response: Response

    try {
        response = (await request) as unknown as Response
    } catch (error: unknown) {
        throw await toServerError(error)
    }

    const blob = await response.blob()
    const name = fileName ?? fileNameFromDisposition(response.headers.get('content-disposition')) ?? 'file'

    saveBlob(blob, name)
}
